import * as exifr from 'exifr';

export interface PreparedImage {
  preview: Blob;
  capturedAt: string;
  width: number;
  height: number;
}

const MAX_DIMENSION = 2048;

const decodeImage = async (file: File): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (nativeError) {
    if (!['image/heic', 'image/heif'].includes(file.type)) throw nativeError;

    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(converted) ? converted[0] : converted;

    return createImageBitmap(blob, { imageOrientation: 'from-image' });
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not create a display copy'))),
      'image/jpeg',
      0.86,
    );
  });

export const captureDateFor = async (file: File) => {
  if (file.type.startsWith('video/')) {
    return new Date(file.lastModified || Date.now()).toISOString();
  }

  try {
    const metadata = await exifr.parse(file, ['DateTimeOriginal', 'CreateDate']);
    const date = metadata?.DateTimeOriginal ?? metadata?.CreateDate;

    if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString();
  } catch {
    // A missing or malformed EXIF block should fall back to the file timestamp.
  }

  return new Date(file.lastModified || Date.now()).toISOString();
};

export const prepareImage = async (file: File): Promise<PreparedImage> => {
  const capturedAt = await captureDateFor(file);
  const image = await decodeImage(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });

  if (!context) throw new Error('This browser cannot prepare images');

  context.drawImage(image, 0, 0, width, height);
  image.close();

  return { preview: await canvasToBlob(canvas), capturedAt, width, height };
};
